import React, { useRef, useEffect } from 'react';
import { Box, Container, Typography, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const Features = () => {
    const scrollRef = useRef(null);

    // Center the scroll position on initial load
    useEffect(() => {
        if (scrollRef.current) {
            const container = scrollRef.current;
            // Calculate the exact middle position
            const middlePosition = (container.scrollWidth - container.clientWidth) / 2;

            // Scroll to the middle instantly when the page loads
            container.scrollTo({ left: middlePosition, behavior: 'instant' });
        }
    }, []);

    // Scroll function for left/right buttons
    const scroll = (direction) => {
        if (scrollRef.current) {
            const scrollAmount = window.innerWidth > 600 ? 400 : 250; // Responsive scroll amount
            if (direction === 'left') {
                scrollRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
            } else {
                scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            }
        }
    };

    // Data for the staggered image gallery
    const galleryData = [
        {
            id: 1,
            // International: Taj Mahal, India
            image: 'https://images.unsplash.com/photo-1564507592333-c60657eea523?auto=format&fit=crop&w=600&q=80',
            alt: 'Taj Mahal - India',
            width: { xs: 180, md: 240 },
            height: { xs: 240, md: 320 },
            marginTop: { xs: 2, md: 8 },
        },
        {
            id: 2,
            // International: Eiffel Tower, Paris
            image: 'https://images.unsplash.com/photo-1511739001486-6bfe10ce785f?auto=format&fit=crop&w=600&q=80',
            alt: 'Eiffel Tower - Paris, France',
            width: { xs: 140, md: 180 },
            height: { xs: 240, md: 360 },
            marginTop: { xs: 0, md: -4 },
        },
        {
            id: 3,
            // National: Kerala Backwaters, India
            image: 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?auto=format&fit=crop&w=800&q=80',
            alt: 'Kerala Backwaters - India',
            width: { xs: 200, md: 320 },
            height: { xs: 260, md: 400 },
            marginTop: { xs: 1, md: 2 },
        },
        {
            id: 4,
            // International: Northern Lights, Iceland
            image: 'https://images.unsplash.com/photo-1579033461380-adb47c3eb938?auto=format&fit=crop&w=600&q=80',
            alt: 'Northern Lights - Iceland',
            width: { xs: 170, md: 260 },
            height: { xs: 250, md: 380 },
            marginTop: { xs: 1, md: -2 },
        },
        {
            id: 5,
            // National: Jaipur Pink City, India
            image: 'https://images.unsplash.com/photo-1477587458883-47145ed94245?auto=format&fit=crop&w=600&q=80',
            alt: 'Hawa Mahal - Jaipur, India',
            width: { xs: 130, md: 180 },
            height: { xs: 220, md: 320 },
            marginTop: { xs: 3, md: 10 },
        },
        {
            id: 6,
            // International: Great Wall of China
            image: 'https://images.unsplash.com/photo-1508804185872-d7badad00f7d?auto=format&fit=crop&w=600&q=80',
            alt: 'Great Wall - China',
            width: { xs: 190, md: 280 },
            height: { xs: 240, md: 340 },
            marginTop: { xs: 0, md: -6 },
        },
        {
            id: 7,
            // National: Varanasi Ghats, India
            image: 'https://images.pexels.com/photos/3581360/pexels-photo-3581360.jpeg?auto=compress&cs=tinysrgb&w=600',
            alt: 'Varanasi Ghats - India',
            width: { xs: 170, md: 240 },
            height: { xs: 250, md: 350 },
            marginTop: { xs: 2, md: 4 },
        },
        {
            id: 8,
            // International: Santorini, Greece
            image: 'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?auto=format&fit=crop&w=600&q=80',
            alt: 'Santorini - Greece',
            width: { xs: 150, md: 220 },
            height: { xs: 220, md: 310 },
            marginTop: { xs: 1, md: -2 },
        },
    ];

    return (
        <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#FAF9F6' }}>
            {/* Section Header */}
            <Container maxWidth="xl" sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 800,
                        fontSize: { xs: '1.8rem', md: '2.5rem' },
                        color: '#1a1a1a',
                        mb: 1,
                    }}
                >
                    Explore Our Destinations Gallery
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        color: '#64748b',
                        fontSize: { xs: '0.95rem', md: '1.05rem' },
                        maxWidth: 500,
                        mx: 'auto',
                    }}
                >
                    A glimpse of the breathtaking places waiting for you
                </Typography>
            </Container>

            <Container maxWidth="xl" sx={{ position: 'relative' }}>

                {/* Left Scroll Button */}
                <IconButton
                    onClick={() => scroll('left')}
                    sx={{
                        position: 'absolute',
                        left: { xs: 4, md: 16 },
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        '&:hover': { bgcolor: 'white' },
                        width: { xs: 36, md: 40 },
                        height: { xs: 36, md: 40 },
                    }}
                >
                    <ArrowBackIosNewIcon sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }} />
                </IconButton>

                {/* Scrollable Container */}
                <Box
                    ref={scrollRef}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: { xs: 2, md: 3 },
                        overflowX: 'auto',
                        scrollBehavior: 'smooth',
                        px: { xs: 6, md: 6 }, // Increased padding on mobile to prevent buttons overlapping images
                        py: { xs: 2, md: 4 },
                        mx: { xs: -2, md: 0 }, // Negative margin to offset container padding on mobile

                        // Hide Scrollbar
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        },
                        '-ms-overflow-style': 'none',
                        'scrollbar-width': 'none',
                    }}
                >
                    {galleryData.map((item) => (
                        <Box
                            key={item.id}
                            sx={{
                                flexShrink: 0,
                                width: item.width,
                                height: item.height,
                                mt: item.marginTop,
                                borderRadius: { xs: '16px', md: '24px' },
                                backgroundImage: `url(${item.image})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                                transition: 'transform 0.3s ease-in-out',
                                '&:hover': {
                                    transform: 'scale(1.02)',
                                },
                            }}
                        />
                    ))}
                </Box>

                {/* Right Scroll Button */}
                <IconButton
                    onClick={() => scroll('right')}
                    sx={{
                        position: 'absolute',
                        right: { xs: 4, md: 16 },
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        '&:hover': { bgcolor: 'white' },
                        width: { xs: 36, md: 40 },
                        height: { xs: 36, md: 40 },
                    }}
                >
                    <ArrowForwardIosIcon sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }} />
                </IconButton>

            </Container>
        </Box>
    );
};

export default Features;